export default class MedicalProcedures {
  constructor(section) {
    this.section = section;
    this.slides = Array.from(section.querySelectorAll("[data-medical-procedures-slide]"));
    this.infoItems = Array.from(section.querySelectorAll("[data-medical-procedures-info]"));

    // Virtual order: slides are re-ordered logically, not moved in DOM
    this.order = []; // order[visualPosition] = slideIndex
    this.activeIndex = 0; // index in this.slides[]
    this.isAnimating = false;
    this.hasRevealed = false;
    this.animationDuration = 1550;

    if (!this.slides.length) {
      return;
    }

    this.init();
  }

  init() {
    // Build initial virtual order: [prev-3, prev-2, prev-1, active, ...]
    // Active slide is at visual position 3 (0-indexed)
    const total = this.slides.length;
    this.order = [];

    for (let i = 0; i < total; i++) {
      // position i: slides[(activeIndex - (VISIBLE_PREV - i) + total) % total]
      this.order.push((this.activeIndex - 3 + i + total * 4) % total);
    }

    // Apply slots instantly (no animation on init)
    this.slides.forEach((slide) => slide.classList.add("is-instant"));
    this.applySlots();

    window.requestAnimationFrame(() => {
      this.slides.forEach((slide) => slide.classList.remove("is-instant"));
    });

    this.section.querySelectorAll("[data-medical-procedures-prev]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.blur();
        this.reveal();
        this.navigate("next");
      });
    });

    this.section.querySelectorAll("[data-medical-procedures-next]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.blur();
        this.reveal();
        this.navigate("prev");
      });
    });

    this.section.querySelectorAll("[data-medical-procedures-play]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.blur();
        this.playActiveVideo(button);
      });
    });

    this.slides.forEach((slide, index) => {
      slide.addEventListener("click", () => {
        if (this.isAnimating) {
          return;
        }

        const slot = slide.dataset.medicalProceduresSlot;

        if (slot !== "prev-1" && slot !== "prev-2" && slot !== "prev-3") {
          return;
        }

        this.reveal();

        // navigate("prev") rotates order RIGHT by 1:
        // prev-1→active in 1 step, prev-2 in 2, prev-3 in 3.
        const stepsMap = { "prev-1": 1, "prev-2": 2, "prev-3": 3 };
        const steps = stepsMap[slot] || 1;

        if (steps === 1) {
          // Adjacent slide: full smooth animation identical to nav button
          this.navigate("prev");
        } else {
          // Farther slides: fast smooth transition (~0.35s)
          this.goDirectly(steps);
        }
      });
    });

    this.observeEntrance();
  }

  observeEntrance() {
    if (!("IntersectionObserver" in window)) {
      this.reveal();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          this.reveal();
          observer.unobserve(this.section);
        });
      },
      {
        rootMargin: "0px 0px -35% 0px",
        threshold: 0.24,
      },
    );

    observer.observe(this.section);
  }

  reveal() {
    if (this.hasRevealed) {
      return;
    }

    this.hasRevealed = true;
    this.section.classList.add("is-revealing");
    this.applySlots(true);
    this.section.classList.add("is-ready");

    window.setTimeout(() => {
      this.section.classList.remove("is-revealing");
    }, this.animationDuration + 520);
  }

  /**
   * Navigate one step in a given direction using infinite wrap.
   * direction: "next" = active moves forward (prev slides come into view)
   *            "prev" = active moves backward
   */
  navigate(direction) {
    if (this.isAnimating || this.slides.length < 2) {
      return;
    }

    this.isAnimating = true;
    this.section.classList.add("is-switching");
    this.pauseAllVideos();

    const total = this.slides.length;

    if (direction === "next") {
      // Rotate order left: the slide at position 0 (prev-3 or hidden tail) wraps to the end
      // Before rotate: [tail, prev-3, prev-2, prev-1, active, ...]
      // We want active to shift left by 1 → rotate left by 1
      const outgoing = this.order.shift();
      const outgoingSlide = this.slides[outgoing];

      // Teleport to entering-right (inactive size, right of active) so it
      // animates in smoothly from the right without the translucent ghost effect.
      outgoingSlide.classList.add("is-instant");
      outgoingSlide.dataset.medicalProceduresSlot = "entering-right";
      void outgoingSlide.offsetWidth;
      outgoingSlide.classList.remove("is-instant");

      // Now push outgoing to end of order
      this.order.push(outgoing);

      // Update activeIndex to new position 3 in order
      this.activeIndex = this.order[3];

      // Fix for >4 slides: if the new active slide is still at its hidden default
      // position, teleport it to entering-right so it enters from the right side
      // instead of animating through the visible area.
      const newActiveSlide = this.slides[this.activeIndex];
      const newActiveSlot = newActiveSlide.dataset.medicalProceduresSlot;
      if (!newActiveSlot || newActiveSlot === "hidden") {
        newActiveSlide.classList.add("is-instant");
        newActiveSlide.dataset.medicalProceduresSlot = "entering-right";
        void newActiveSlide.offsetWidth;
        newActiveSlide.classList.remove("is-instant");
      }

    } else {
      // Rotate order right: the slide at the far end wraps to position 0
      const incoming = this.order.pop();
      const incomingSlide = this.slides[incoming];

      // Teleport incoming slide instantly to left (off-screen, no scale) so it enters smoothly
      incomingSlide.classList.add("is-instant");
      incomingSlide.dataset.medicalProceduresSlot = "entering-left";
      void incomingSlide.offsetWidth;
      incomingSlide.classList.remove("is-instant");

      this.order.unshift(incoming);
      this.activeIndex = this.order[3];
    }

    // Apply new slots (with animation)
    this.applySlots(true);

    window.setTimeout(() => {
      this.isAnimating = false;
      this.section.classList.remove("is-switching");
      // Clean up — remove any leftover exiting-right states
      this.applySlots(false);
    }, this.animationDuration);
  }

  /**
   * Jump to the slide N right-rotations away with a fast smooth transition.
   * Used for prev-2 and prev-3 clicks.
   */
  goDirectly(steps) {
    if (this.isAnimating || this.slides.length < 2 || steps <= 0) {
      return;
    }

    const FAST_DURATION = 650;

    this.isAnimating = true;
    this.section.classList.add("is-switching");
    this.pauseAllVideos();

    // Rotate order RIGHT by `steps` (same direction as navigate("prev"))
    for (let i = 0; i < steps; i++) {
      const incoming = this.order.pop();
      this.order.unshift(incoming);
    }
    this.activeIndex = this.order[3];

    // Apply fast transition on all slides via inline style override
    const fastStyle = `transition-duration: ${FAST_DURATION}ms !important`;
    this.slides.forEach((slide) => slide.setAttribute("style", fastStyle));
    this.applySlots(false);

    window.setTimeout(() => {
      this.slides.forEach((slide) => slide.removeAttribute("style"));
      this.isAnimating = false;
      this.section.classList.remove("is-switching");
      // Update info panel after slides settle
      this.applySlots(true);
    }, FAST_DURATION + 50);
  }

  /**
   * Assigns data-slot attributes based on current virtual order.
   * Virtual positions: 0 = prev-3, 1 = prev-2, 2 = prev-1, 3 = active, rest = hidden.
   *
   * Key rule: if a slide is transitioning from entering-right/entering-left to hidden,
   * snap it instantly to avoid the visible "fly-back" artifact.
   */
  applySlots(animateInfo = false) {
    const SLOT_NAMES = ["prev-3", "prev-2", "prev-1", "active"];
    const TRANSIENT_SLOTS = new Set(["entering-right", "entering-left"]);

    // Build slot map from virtual order
    const slotMap = new Map();

    this.order.forEach((slideIndex, visualPos) => {
      slotMap.set(slideIndex, SLOT_NAMES[visualPos] || "hidden");
    });

    this.slides.forEach((slide, index) => {
      const newSlot = slotMap.get(index) || "hidden";
      const currentSlot = slide.dataset.medicalProceduresSlot || "hidden";
      const isActive = newSlot === "active";

      if (currentSlot !== newSlot) {
        // If a slide was in a transient entry position and now goes hidden,
        // snap it instantly to prevent the visible reverse-fly artifact.
        if (TRANSIENT_SLOTS.has(currentSlot) && newSlot === "hidden") {
          slide.classList.add("is-instant");
          slide.dataset.medicalProceduresSlot = newSlot;
          void slide.offsetWidth;
          slide.classList.remove("is-instant");
        } else {
          slide.dataset.medicalProceduresSlot = newSlot;
        }
      }

      slide.classList.toggle("is-active", isActive);

      if (!isActive) {
        slide.classList.remove("is-playing");
        this.resetVideo(slide);
      }

      if (animateInfo && isActive) {
        slide.classList.add("is-promoting");

        window.setTimeout(() => {
          slide.classList.remove("is-promoting");
        }, this.animationDuration);
      } else if (!animateInfo) {
        slide.classList.remove("is-promoting");
      }
    });

    // Info panel
    this.infoItems.forEach((item) => {
      const isActive = Number(item.dataset.medicalProceduresInfo) === this.activeIndex;

      item.classList.toggle("is-active", isActive);

      if (animateInfo && isActive) {
        item.classList.remove("is-revealing");

        window.requestAnimationFrame(() => {
          item.classList.add("is-revealing");
        });

        window.setTimeout(() => {
          item.classList.remove("is-revealing");
        }, 1600);
      }
    });
  }

  playActiveVideo(button) {
    const slide = button.closest("[data-medical-procedures-slide]");

    if (!slide || Number(slide.dataset.medicalProceduresSlide) !== this.activeIndex || this.isAnimating) {
      return;
    }

    const video = slide.querySelector("[data-medical-procedures-video]");

    if (!video) {
      return;
    }

    if (!video.getAttribute("src") && video.dataset.videoSrc) {
      video.setAttribute("src", video.dataset.videoSrc);
      video.load();
    }

    video.controls = true;
    slide.classList.add("is-playing");

    const playPromise = video.play();

    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        video.controls = false;
        slide.classList.remove("is-playing");
      });
    }
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      slide.classList.remove("is-playing");
      this.resetVideo(slide);
    });
  }

  resetVideo(slide) {
    const video = slide.querySelector("[data-medical-procedures-video]");

    if (!video) {
      return;
    }

    video.pause();
    video.currentTime = 0;
    video.controls = false;

    if (video.getAttribute("src")) {
      video.removeAttribute("src");
      video.load();
    }
  }
}

document.querySelectorAll(".medical-procedures").forEach((section) => {
  new MedicalProcedures(section);
});
